import { addEvent, clearEvents, liveEnded, addMessage, addLocalLink, notedScheduledRun, setStation, setThreadId, setViewer, setWaiting, setPrompting, setConnectionStatus, setPendingResumeThreadId, setSimFact, setCardPush, setWidget, addTypingPeer, removeTypingPeer, clearTypingPeers, setTransientNotice, setFloatingNotice, setAssistantStatus, setSplitToken, setObserveStatus, markEventsStale } from './chatSlice';
import { subscribeStation, sendText, sendOpenThread, resumeThread, sendObserveThread, sendObserveStation, sendObserveEmbed, sendObserveScheduled, requestMailDelivery } from './Actions';

// card-sandbox-messaging-push: a monotonic nonce stamped on each card-push
// dispatch so an identical repeated push (same kind + message) still changes the
// Redux payload → the receiving card's onPush effect re-fires (a bare value
// compare would otherwise swallow the duplicate).
let cardPushSeq = 0;

// Station page shapes under /s/ (station-url-hash):
//
//   /s/<url_token>                 the chat page — an opaque per-station token
//   /s/<url_token>/<share_token>   output-second-screen observer mode
//   /s/<station_id>/view           token-less board for a display device
//   /s/<station_id>/scheduled      token-less board — the viewer's own scheduled output
//   /embed/s/<station_id>          interactive embed (numeric) — see isEmbed below
//
// The page can't map a token to a station itself: the BFF resolves it and stamps
// window.__AVEQ_STATION__ = {id, network, token} into the document. Any other
// numeric shape is a 404 at the BFF and never reaches here.
const parseStationPath = () => {
  // Guard a missing pathname — a test may replace window.location with a bare stub.
  const path = window.location && window.location.pathname;
  if (!path) return null;
  const m = path.match(/^(\/embed)?\/s\/([^/?#]+)(?:\/([^/?#]+))?/);
  if (!m) return null;
  const [, embed, first, second] = m;

  if (/^\d+$/.test(first)) {
    if (embed) return { stationId: parseInt(first, 10), outputToken: second || null };
    // `view` / `scheduled` are sentinels the consumer special-cases, not share tokens.
    if (second === 'view' || second === 'scheduled') {
      return { stationId: parseInt(first, 10), outputToken: second };
    }
    return null;
  }
  // A non-numeric /embed/s/<id> is an opaque embed-record token, not a station.
  if (embed) return null;
  const stamped = window.__AVEQ_STATION__;
  if (!stamped || stamped.token !== first || !stamped.id) return null;
  return {
    stationId: parseInt(stamped.id, 10),
    network: stamped.network || null,
    urlToken: first,
    outputToken: second || null,
  };
};

// reinvoke-embed-actions: an ACTABLE re-invoke embed learns its station from the
// /api/embed/s/<id> response (the URL's <id> is an opaque record token — nothing to
// parse), so ChatInterface sets it here BEFORE dispatching connectWebSocket and the
// onOpen SUBSCRIBE_STATION targets the record's station instead of the localStorage
// fallback (which would subscribe some unrelated station). Module-scoped, page-lived:
// an embed page hosts exactly one record.
let embedStationOverride = null;
export const setEmbedStation = (id) => { embedStationOverride = id; };
// The station showing a card: an embed's record station, else the chat page's.
export const getContextStationId = () => (isEmbed() ? embedStationOverride : getPathStationId());

const getStationId = () => {
  if (isReinvokeEmbed() && embedStationOverride != null) return embedStationOverride;
  const parsed = parseStationPath();
  // embeddable-card-live-action: localStorage can THROW in a partitioned /
  // third-party iframe context (Safari ITP, a file:// parent → Chrome "access is
  // denied"). It's a convenience cache for the station id, never load-bearing —
  // fall back to the URL-parsed id (or the default) rather than crash the SPA.
  try {
    if (parsed && parsed.stationId) {
      localStorage.setItem('stationId', parsed.stationId);
      return parsed.stationId;
    }
    const saved = localStorage.getItem('stationId');
    return saved ? parseInt(saved, 10) : 1012;
  } catch (_) {
    return (parsed && parsed.stationId) || 1012;
  }
};

// output-second-screen slug: the share token in /s/<url_token>/<share_token>; its
// presence is the output-only "observer" mode signal.
export const getOutputToken = () => {
  const parsed = parseStationPath();
  return parsed ? parsed.outputToken : null;
};

// /embed/s/<id> is the third-party embed mode — the output-only, chrome-trimmed
// SPA on an external page via an <iframe>. There are TWO shapes, distinguished by
// whether <id> is numeric (embeddable-card-live-action):
//   • /embed/s/<numeric station_id> — the INTERACTIVE / legacy shape: observe the
//     station's live output (config.embed_public) + optional guest-act.
//   • /embed/s/<token> — the NEW read-only re-invoke shape: fetch one card from
//     /api/embed/s/<token> (re-invokes the stored action_spec server-side), no WS.
export const isEmbed = () => window.location.pathname.startsWith('/embed/s/');

// The embed-id segment from /embed/s/<id> (numeric station id OR opaque token).
export const getEmbedId = () => {
  const m = window.location.pathname.match(/^\/embed\/s\/([^/?#]+)/);
  return m ? m[1] : null;
};

// embeddable-card-live-action: TRUE for the re-invoke shape only — a non-numeric
// (token_urlsafe) embed id. Numeric ids are the interactive/legacy observe shape,
// which keeps the WS path. The re-invoke shape fetches its card and never opens a
// WS, so this is the switch ChatInterface uses to pick fetch-vs-connect.
export const isReinvokeEmbed = () => {
  if (!isEmbed()) return false;
  const id = getEmbedId();
  return !!id && !/^\d+$/.test(id);
};

// nav-mobile-menu: the station's canonical network, from the BFF stamp on a
// /s/<url_token> page. Null elsewhere — the caller falls back to the all-networks page.
export const getStationNetwork = () => {
  const parsed = parseStationPath();
  return parsed && parsed.network ? parsed.network : null;
};

// The current page's /s/<url_token> path, or null off a chat page.
export const getStationUrlPath = () => {
  const parsed = parseStationPath();
  return parsed && parsed.urlToken ? `/s/${parsed.urlToken}` : null;
};

// tobin-order-at-seller: the station id of the page we're ON, straight from the
// URL — or null when this isn't a station chat (dashboard, gallery, a token-shaped
// re-invoke embed). Deliberately NOT getStationId(): that one falls back to
// localStorage and finally to a hardcoded default, so an unrelated page would
// claim to be "on" some station. A card comparing itself against the current
// station needs the honest answer, and null ("not on a station page") is a real,
// meaningful value here.
export const getPathStationId = () => {
  const parsed = parseStationPath();
  return parsed && parsed.stationId ? parsed.stationId : null;
};

// kiosk-browser-display: `?shared=1` on the /s/<id>/view URL asks the BFF for
// the SHARED observe (station's latest output from anyone, not just this
// account) — what an unattended kiosk's display account needs, since it never
// chats and so never has its own thread. Only meaningful alongside the `view`
// sentinel; the BFF gates it (station config.secondary_display + permission),
// so a hand-typed ?shared=1 can at worst yield OBSERVE_DENIED.
export const getViewShared = () => {
  try {
    return new URLSearchParams(window.location.search).get('shared') === '1';
  } catch {
    return false;
  }
};

// station-entry-params: `?entry=<value>` on a station chat URL — the deep-link
// entry primitive (ADR 2026-08-14-mesh-ordering D3). The value is an opaque id
// the AGENT validates (strict charset, station-config-declared function only);
// the SPA just carries it into the assist:thread open, ONCE: consumeEntryValue
// strips it from the URL via replaceState so reloads/reconnects can't re-fire
// the invoke. Observe/embed/view shapes never read it.
export const getEntryValue = () => {
  try {
    return new URLSearchParams(window.location.search).get('entry') || null;
  } catch {
    return null;
  }
};

// card-continue-at-station: `?with=<ref>` opens the chat with a card as context, and
// `?say=<text>` is the person's opening line (a Continue button such as "Reserve").
// Taken off the URL once, like ?entry; `say` waits until the new thread is bound.
let pendingSay = null;

export const consumeContinueParams = () => {
  let bring = null;
  try {
    const url = new URL(window.location.href);
    bring = url.searchParams.get('with');
    const say = url.searchParams.get('say');
    if (!bring) return null;
    pendingSay = say && say.trim() ? say.trim().slice(0, 200) : null;
    url.searchParams.delete('with');
    url.searchParams.delete('say');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch {
    // URL/history unavailable: still open with the card.
  }
  return bring;
};

export const takePendingSay = () => {
  const say = pendingSay;
  pendingSay = null;
  return say;
};

// station-open-session: `?open=<token>`, taken off the URL once like ?entry.
export const consumeOpenToken = () => {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('open');
    if (!token) return null;
    url.searchParams.delete('open');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    return token;
  } catch {
    return null;
  }
};

export const consumeEntryValue = () => {
  const value = getEntryValue();
  if (!value) return null;
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('entry');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch {
    // URL/history unavailable (tests, odd embeds) — still send the value;
    // worst case a manual reload re-opens with it, and the agent's own
    // fail-closed validation bounds what that can do.
  }
  return value;
};

// kiosk-send-split-link: `?fallback=<station_id>` on a token view URL — the
// kiosk's home station, stamped by the device supervisor. When the pinned
// token is denied/expired, the SPA navigates itself back to the home
// station's shared view instead of parking on the denial screen (the
// supervisor can't see page state; the SPA is where the denial is visible).
// Numeric-only — anything else is ignored.
export const getFallbackStation = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get('fallback');
    return raw && /^\d+$/.test(raw) ? parseInt(raw, 10) : null;
  } catch {
    return null;
  }
};

// envelope-key-resend: how long an OPEN socket may wait for SUBSCRIBER_METRICS
// before the handshake watchdog retries (see onHandshakeTimeout). A healthy
// handshake lands in well under a second; a cold cross-network one in ~2s.
export const HANDSHAKE_TIMEOUT_MS = 10000;
// Review !738 #3: the RETRY socket waits longer before giving up to the Reconnect
// overlay — after a root recreate the mesh can take ~60-90s to settle (the
// self-healing `bad proof` window), and a slow-but-healthy start shouldn't get a
// modal at 20s.
export const HANDSHAKE_RETRY_TIMEOUT_MS = 30000;

const websocketMiddleware = (store) => {
  let socket = null;
  // chat-helper-completion slug (#3): per-peer clear-timers for the typing
  // indicator. Each TYPING frame (re)arms a 5s timer; when it fires with no
  // refresh, the peer's "is typing" is removed. Keyed by display name.
  const typingTimers = {};
  // chat-helper-completion slug (#5): true once the socket has dropped, so the
  // next successful handshake is recognised as a RECONNECT (not first connect)
  // and shows the transient "Reconnected" notice.
  let wasStale = false;
  // restart-session-reset: `contextReset` is stamped on EVERY frame of the
  // fall-through reply, so the clear must fire once per episode, not per frame.
  let contextResetDone = false;
  let transientTimer = null;
  // envelope-key-resend: handshake watchdog. The socket can OPEN yet the
  // SUBSCRIBER_METRICS reply never arrive — the SUBSCRIBE_STATION was lost in the
  // mesh (seen: sealed under a recreated root's stale envelope key). Before this
  // the page sat on "Connecting to Ave Q..." until a manual reload. Now: one
  // silent retry on a fresh socket (what the reload did), then give up to 'stale'
  // so the Reconnect overlay shows. Not a reconnect-on-drop — ws-reconnect's
  // "no auto-reconnect" is about a LIVE socket dying; this is a handshake that
  // never completed.
  let handshakeTimer = null;
  let handshakeRetried = false;
  let socketUrl = null;

  const clearHandshakeTimer = () => {
    if (handshakeTimer) clearTimeout(handshakeTimer);
    handshakeTimer = null;
  };

  const openSocket = (url) => {
    socketUrl = url;
    const s = new WebSocket(url);
    socket = s;
    s.onmessage = onMessage;
    s.onclose = () => onClose(s);
    s.onopen = onOpen;
  };

  // Review !738 #2: close a replaced socket WITHOUT its handlers, so its late
  // close event can't clear the new socket's handshake watchdog or flip the page
  // to 'stale' mid-handshake.
  const closeDetached = (s) => {
    s.onclose = null;
    s.onmessage = null;
    s.onopen = null;
    s.close();
  };

  const onHandshakeTimeout = () => {
    handshakeTimer = null;
    if (!socket) return;
    if (!handshakeRetried) {
      handshakeRetried = true;
      console.warn(`[ws] no SUBSCRIBER_METRICS within ${HANDSHAKE_TIMEOUT_MS}ms; retrying on a fresh socket`);
      closeDetached(socket);
      openSocket(socketUrl);
      return;
    }
    console.warn('[ws] handshake retry also stalled; marking connection stale');
    socket.close();   // handlers attached → onClose → 'stale' → Reconnect overlay
  };

  const onOpen = () => {
    // 'connecting' bridges the gap until SUBSCRIBER_METRICS lands.
    // The MAIL handler flips to 'live' once the first thread / resume
    // ack arrives. SubscribeStation kicks the server-side flow that
    // produces SUBSCRIBER_METRICS.
    store.dispatch(setConnectionStatus('connecting'));
    store.dispatch(subscribeStation(getStationId()));
    clearHandshakeTimer();
    handshakeTimer = setTimeout(onHandshakeTimeout,
      handshakeRetried ? HANDSHAKE_RETRY_TIMEOUT_MS : HANDSHAKE_TIMEOUT_MS);
  };

  const onClose = (closed) => {
    // Only the CURRENT socket's close disarms the watchdog. A replaced socket
    // closing late (WS_DISCONNECT → WS_CONNECT 50ms later, as AuthHeader does)
    // must not cancel the new socket's handshake timer (review !738 #2). The rest
    // of onClose still runs for it — the resume-thread stash is wanted there.
    if (closed === undefined || closed === socket) clearHandshakeTimer();
    // No reconnect from here: the page decides (tap, or a return to the tab).
    // Mark the connection stale + stash the current threadId so the next user-initiated
    // reconnect can ask the agent to rebind the existing ConversationLog
    // rather than start a fresh thread.
    const currentThreadId = store.getState().chat.threadId;
    if (currentThreadId) {
      store.dispatch(setPendingResumeThreadId(currentThreadId));
    }
    // chat-helper-completion slug (#5): remember we dropped, so the next
    // handshake shows a transient "Reconnected" notice rather than treating it
    // as a first connect.
    wasStale = true;
    // chat-helper-completion slug (#3): the socket is gone, so no more TYPING
    // frames will arrive to refresh or clear anyone's indicator — cancel the
    // pending per-peer timers and drop all typing presence now, rather than
    // leaving a stuck "… is typing…" line for up to 5s.
    Object.values(typingTimers).forEach(clearTimeout);
    for (const k of Object.keys(typingTimers)) delete typingTimers[k];
    store.dispatch(clearTypingPeers());
    // ephemeral-status-beats: drop any transient "working…" status on disconnect
    // so it doesn't stick after the socket dies mid-turn.
    store.dispatch(setAssistantStatus(null));
    store.dispatch(setConnectionStatus('stale'));
  };

  const onMessage = (event) => {
    const currentState = store.getState();
    const data = JSON.parse(event.data);
    console.log("Incoming message: ", JSON.stringify(data))
    switch (data.messageType) {
      case 'SUBSCRIBER_METRICS': {
        clearHandshakeTimer();
        handshakeRetried = false;
        const stationId = data.metrics.station.station.station_id
        // reinvoke-embed-actions: an ACTABLE re-invoke embed connects this WS ONLY
        // as its card's action channel — the subscribe gives the sandboxed card its
        // station identity (setStation → SandboxedCard's dedicated invoke socket)
        // and, for an anonymous viewer on an embed_act station, minted the guest
        // grant server-side. It must neither OBSERVE nor start a thread: the tile's
        // content is the re-invoke fetch, so nothing on this socket may push OUTPUT
        // frames into `events` (an observe would clobber the fetched cards).
        if (isReinvokeEmbed()) {
          store.dispatch(setStation(data));
          store.dispatch(setConnectionStatus('live'));
          break;
        }
        store.dispatch(setWaiting(true));
        // If we have a pending resume from a prior session, ask the
        // agent to rebind that thread's ConversationLog to this new
        // qbric session. If the thread is gone (agent restarted), the
        // server-side handler falls through to fresh-thread creation —
        // either way, the next MAIL carries a valid threadId. Reset
        // pendingResumeThreadId so a subsequent disconnect doesn't try
        // to resume the wrong session.
        // output-second-screen slug: a second-screen observer (URL carries a
        // share token) joins the OWNER's thread read-only instead of creating
        // or resuming its own. The BFF redeems the token (same-account + cap)
        // and thereafter mirrors the owner's OUTPUT frames to this socket.
        const outputToken = getOutputToken();
        if (isEmbed()) {
          // embeddable-card-live-action: this branch is reached ONLY by the
          // INTERACTIVE / legacy embed shape `/embed/s/<numeric station_id>` — it
          // observes the station's latest output anonymously (config.embed_public)
          // and, for an embed_act station, the socket's SUBSCRIBE_STATION minted a
          // guest grant so the card can act. A NEW read-only embed is
          // `/embed/s/<token>`, which fetches /api/embed/s/<token> and never opens
          // this WS (see ChatInterface's embed bootstrap), so it never reaches here.
          store.dispatch(sendObserveEmbed(stationId, data.sessionId));
        } else if (outputToken === 'view') {
          // /s/<id>/view — token-less stable URL: observe this station's output
          // (same-account; with ?shared=1 the kiosk-browser-display shared
          // variant — station-wide, BFF-gated).
          store.dispatch(sendObserveStation(stationId, data.sessionId, getViewShared()));
        } else if (outputToken === 'scheduled') {
          // scheduled-output-screen slug: /s/<id>/scheduled — token-less, observe
          // the viewer's OWN per-station scheduled-output board.
          store.dispatch(sendObserveScheduled(stationId, data.sessionId));
        } else if (outputToken) {
          store.dispatch(sendObserveThread(outputToken, stationId, data.sessionId));
        } else {
          const resumeId = currentState.chat.pendingResumeThreadId;
          if (resumeId) {
            // Reconnect rebind — never carries ?entry (the agent-side rule too:
            // entry fires on assist:thread opens only).
            store.dispatch(resumeThread(stationId, data.sessionId, resumeId));
            store.dispatch(setPendingResumeThreadId(null));
          } else {
            // station-entry-params: a deep-link open carries the entry value on
            // the thread-open frame, one-shot (consumeEntryValue strips the URL).
            const entryValue = consumeEntryValue();
            const bring = consumeContinueParams();
            const open = consumeOpenToken();
            store.dispatch(entryValue || bring || open
              ? sendOpenThread(stationId, data.sessionId, entryValue, bring, open)
              : sendText("thread", "", stationId, data.sessionId));
          }
        }
        store.dispatch(setStation(data));
        // Flip to 'live' now that the wire handshake is past — the
        // user can submit messages even before the agent's welcome /
        // resume ack lands.
        store.dispatch(setConnectionStatus('live'));
        // chat-helper-completion slug (#5): if this handshake follows a drop,
        // surface an ephemeral "Reconnected" notice that auto-clears. Transient
        // only — never pushed into `messages`, so it can't replay later.
        if (wasStale) {
          wasStale = false;
          store.dispatch(setTransientNotice('Reconnected'));
          if (transientTimer) clearTimeout(transientTimer);
          transientTimer = setTimeout(() => store.dispatch(setTransientNotice(null)), 3000);
        }
        break;
      }
      case 'MAIL': {
        const mailPayload = data.mail.payload;
        const mailMessage = mailPayload.message || '';
        const meta = mailPayload.meta || {};

        // stale-output-after-restart: a reconnect asked to resume a thread the agent no
        // longer has (it restarted) and got a FRESH one. The card on the output panel
        // belongs to a conversation the assistant no longer remembers — "Save this" on
        // it could only answer "nothing to save yet" — so clear it. The chat scrollback
        // stays as cosmetic history (agent-side rule, entry.py resume fall-through).
        if (meta.contextReset) {
          // restart-session-reset: the agent restarted, so this thread is new and
          // remembers nothing.
          //
          // A PRIVATE session's transcript lived only in the agent, so it is gone —
          // clear the whole display, because chat kept as "cosmetic history" is
          // conversation the assistant cannot see ("Save this" on it answers
          // "nothing to save yet"). A ROOM's transcript is durable in the station's
          // own message log and is replayed right after this, so it is NOT gone;
          // only @aveq's memory of it is. Clearing it there would be a lie, and the
          // wording has to differ too.
          const _cfg = (((store.getState().chat.station || {}).station || {}).config) || {};
          const roomChat = _cfg.cardinality === 'shared' || _cfg.resolver_mode === 'broadcasting';
          if (contextResetDone) {
            store.dispatch(clearEvents());
          } else {
            contextResetDone = true;
            store.dispatch(addMessage({
              clear: !roomChat, isSystem: true, userId: 0,
              message: roomChat
                ? 'The assistant restarted — it won’t remember what was said earlier in this room.'
                : 'The assistant restarted. This is a new session — the earlier conversation is gone.',
            }));
            if (roomChat) store.dispatch(clearEvents());
          }
        } else {
          contextResetDone = false;
        }

        // station-broadcast slug: a room message from another live visitor
        // (or a StationChat announcement). Render it tagged with the sender;
        // do NOT disturb the recipient's own AI-thread state (threadId /
        // viewer / prompting / widget) or the waiting flag — it isn't a
        // reply to anything they asked.
        if (meta.room) {
          // station-chat-subscriptions slug: join-replay resends the joiner's
          // OWN past messages too. The agent marks those with meta.mine (it
          // compares sender_name to the joiner's session display name — a
          // reliable signal where a frontend profile-name compare isn't). Render
          // them as the user's own bubble (right-aligned, no sender tag) so
          // replayed history matches live styling.
          const mine = !!meta.mine;
          store.dispatch(addMessage({
            userId: 0,
            displayName: meta.sender || 'Someone',
            senderAccount: meta.senderAccount || null,
            room: !mine,
            isLocal: mine,
            // chat-helper-completion slug (#2): the message's original
            // wall-clock (live broadcast + replayed history both carry it).
            ts: meta.ts,
            // Stable id (meta.msgId = SQLite row id) when present, so the
            // reducer dedups a live message against its later replay (and
            // replay-vs-replay on reconnect). Falls back to the wrapped id.
            msgId: meta.msgId || mailPayload.id,
            message: mailMessage,
            actions: mailPayload.actions,
            // station-chat-load-older: a backfill page prepends to the top
            // (older history) instead of appending like live/join replay.
            older: !!meta.older,
          }));
          break;
        }

        // session-identity-resume slug: a PRIVATE (1-1) session's history replayed
        // on resume — especially a full reload, which cleared the in-memory
        // scrollback. (Room replays already returned above; this only sees non-room
        // ones.) Render each row as chat scrollback — `mine` right-aligns the user's
        // own turns, else the assistant — WITHOUT touching threadId/viewer/waiting.
        // The trailing "Welcome back" carries threadId to rebind state.
        //
        // session-output-panel slug (D5): the SAME branch also carries the replayed
        // OUTPUT-PANEL cards — a single replay frame whose `message` is "" and whose
        // `actions[0].params.history.options` hold the panel's pre-reload cards.
        // addMessage extracts those into state.events (then returns early on the
        // empty message, adding no chat row), restoring the output panel without the
        // submit-time stale/reset dance. KEEP passing `actions` through — dropping it
        // (as a "text-only replay" cleanup might) would silently break card replay.
        if (meta.replay) {
          const mine = !!meta.mine;
          store.dispatch(addMessage({
            userId: 0,
            room: false,
            isLocal: mine,
            ts: meta.ts,
            msgId: meta.msgId || mailPayload.id,
            message: mailMessage,
            actions: mailPayload.actions,
            replay: true,
          }));
          break;
        }

        // card-sandbox-messaging-push: a kind-targeted push to a sandboxed card's
        // onPush (meta.cardPush + meta.cardKind). Route it OFF the chat stream to the
        // matching card BY KIND — it must NOT touch chat scrollback, waiting, status,
        // threadId, or viewer. A monotonic `seq` guarantees an identical repeated push
        // (e.g. the same score twice) still fires the receiving card's effect.
        if (meta.cardPush) {
          store.dispatch(setCardPush({ kind: meta.cardKind, message: mailMessage, seq: ++cardPushSeq,
                                       liveEvent: meta.liveEvent || null }));
          break;
        }

        // A live event ended (live_events): its card closes, its link says so.
        if (meta.liveEnded) {
          store.dispatch(liveEnded(meta.liveEnded));
          break;
        }

        // The turn's cards could not become a saved link: link to the page's own copy.
        if (meta.localLink) {
          store.dispatch(addLocalLink({ displayName: currentState.chat.station.station
            && currentState.chat.station.station.name }));
          break;
        }

        store.dispatch(setWaiting(false));

        // ephemeral-status-beats: a transient "working…" status (e.g. "checking…").
        // Show it in the status line; do NOT add it to the chat log. Any real
        // reply below clears it via setAssistantStatus(null).
        if (meta.ephemeral) {
          if (meta.threadId) store.dispatch(setThreadId(meta.threadId));
          store.dispatch(setAssistantStatus(mailMessage));
          break;
        }
        // A notice for this viewer: floated over the chat, never a bubble.
        if (meta.notice) {
          if (meta.threadId) store.dispatch(setThreadId(meta.threadId));
          store.dispatch(setFloatingNotice(mailMessage));
          break;
        }
        store.dispatch(setAssistantStatus(null));   // a real reply arrived — clear any status

        // simFact routes to SimulatorViewer instead of chat — short-circuit.
        if (meta.simFact) {
          store.dispatch(setSimFact(meta.simFact));
          break;
        }

        if (meta.threadId) {
          store.dispatch(setThreadId(meta.threadId));
        }
        if (meta.viewer) {
          store.dispatch(setViewer(meta.viewer));
        }
        store.dispatch(setPrompting(!!meta.prompting));
        store.dispatch(setWidget(meta.widget || null));

        // When the helper's viewer is "game" or "simulator" and the
        // payload is a kind-routed structured card, route it to the
        // viewer-panel events list instead of the chat scroll — those
        // viewers render the card via CardFor and the chat would
        // otherwise show the same card twice (or render the raw JSON).
        const isPanelViewer = meta.viewer === 'game' || meta.viewer === 'simulator';
        let routedToPanel = false;
        if (isPanelViewer && mailMessage) {
          try {
            const parsed = typeof mailMessage === 'object' ? mailMessage : JSON.parse(mailMessage);
            if (parsed && parsed.kind) {
              store.dispatch(addEvent(mailMessage));
              routedToPanel = true;
            }
          } catch (e) { /* not JSON — fall through to chat */ }
        }

        // station-empty-greeting: meta.silent is a rebind-only frame — threadId /
        // viewer were applied above, but it must NOT render a chat bubble (used
        // for the resume ack on a "No greeting" station).
        if (!routedToPanel && !meta.silent && meta.transient) {
          // resume-ack-transient: the visible "Welcome back" resume ack rebinds state
          // (threadId/viewer were applied above) but must NOT persist in the scrollback
          // — it would stack up a bubble on every reconnect. Surface it as an
          // auto-clearing notice, like the "Reconnected" toast.
          store.dispatch(setTransientNotice(mailMessage));
          if (transientTimer) clearTimeout(transientTimer);
          transientTimer = setTimeout(() => store.dispatch(setTransientNotice(null)), 4000);
        } else if (!routedToPanel && !meta.silent) {
          store.dispatch(addMessage({
              userId: 0,
              displayName: currentState.chat.station.station.name,
              msgId: mailPayload.id,
              message: mailMessage,
              actions: mailPayload.actions,
              // chat-ux-polish /clear: wipe the scrollback + output panel before
              // rendering this (confirmation) message.
              clear: !!meta.clear,
            }));
        }

        break;
      }
      case 'PAYLOAD_MESSAGE':
        // ephemeral-status-beats: a qlink PAYLOAD reply (e.g. a sim-state update
        // from a SimulatorViewer action) is a real response that arrives with NO
        // MAIL frame — clear any transient status here too, so a viewer action's
        // confirmation ("checking…") can't linger on this non-MAIL reply path.
        store.dispatch(setAssistantStatus(null));
        // Handle sim state responses directly from qlink
        if (data.ref && data.ref.startsWith('sim_state_') && data.payload) {
          const result = data.payload.find(p => p.name === 'result');
          if (result && result.key) {
            store.dispatch(setSimFact(result.key));
          }
        }
        // tile-card-actions: on a RE-INVOKE page this is the ref-correlated
        // reply of a card invoke (a Tobin claim's celebration, or a helper's
        // refusal). It was silently dropped before — surface the SERVER's
        // words as the page toast; that's the ack, not optimistic UI.
        // canvas-card (tile pass): an INLINE card's reply carries the helper's emitted
        // BOARD(S) whole (`cards`). They REPLACE the page's events under the SAME
        // embed generation, so CardViewer's key does not change and the card is not
        // remounted — new props, the way a chat panel delivers them, and Canvas's
        // prefix rule keeps the playhead. Nothing is toasted over a board.
        if (isReinvokeEmbed() && data.payload) {
          const boards = data.payload.find((e) => e.name === 'cards');
          if (boards) {
            try {
              const cards = JSON.parse(boards.key);
              const prev = (currentState.chat.events || []).slice().reverse()
                .find((e) => e && typeof e === 'object' && e.__embedGen != null);
              const gen = prev ? prev.__embedGen : undefined;
              store.dispatch(clearEvents());
              cards.forEach((c) => store.dispatch(addEvent(
                c && typeof c === 'object' && gen != null ? { ...c, __embedGen: gen } : c)));
            } catch (e) { /* malformed cards — leave the board as it was */ }
            break;
          }
        }
        if (isReinvokeEmbed() && data.payload) {
          try {
            const err = data.payload.find((e) => e.name === 'error');
            const res = data.payload.find((e) => e.name === 'result');
            let text = (err && err.key) || (res && res.key) || '';
            try { const j = JSON.parse(text); text = j.error || j.result || text; } catch (e) { /* plain */ }
            if (text) {
              store.dispatch(setTransientNotice(String(text)));
              setTimeout(() => store.dispatch(setTransientNotice(null)), 6000);
            }
          } catch (e) { /* malformed payload — no toast */ }
        }
        break;
      case 'TYPING': {
        // chat-helper-completion slug (#3): another visitor is typing. Show
        // their name; (re)arm a 5s timer so it clears on its own if no further
        // ping arrives (and addMessage clears it the instant their message
        // lands). Purely ephemeral — nothing enters the message list.
        const name = data.displayName || 'Someone';
        store.dispatch(addTypingPeer(name));
        if (typingTimers[name]) clearTimeout(typingTimers[name]);
        typingTimers[name] = setTimeout(() => {
          store.dispatch(removeTypingPeer(name));
          delete typingTimers[name];
        }, 5000);
        break;
      }
      // output-second-screen slug: the owner's ?split mint reply — carries the
      // share token the SPA renders as the /s/<id>/<token> link/QR.
      case 'SPLIT_TOKEN':
        store.dispatch(setSplitToken(data.token || null));
        break;
      // output-second-screen slug: observer-side join result.
      case 'OBSERVING':
        store.dispatch(setObserveStatus('observing'));
        // embeddable-card-interactive: adopt the observed thread's id so an embedded card's
        // invoke can stamp aveq_id = threadId (and the milestone push routes back to this
        // thread's observers). Read-only embeds simply never invoke; harmless there.
        if (data.threadId) store.dispatch(setThreadId(data.threadId));
        break;
      // output-second-screen slug: the owner started a new turn — soft-clear the
      // output panel so the next frame replaces rather than piles up (matches the
      // owner's markEventsStale-on-submit). Used by the read-only view screens.
      case 'OUTPUT_RESET':
        store.dispatch(markEventsStale());
        break;
      case 'OBSERVE_DENIED':
        store.dispatch(setObserveStatus({ denied: true, reason: data.reason || 'denied' }));
        break;
      // kiosk-send-split-link: result of "Pin to display". Config-prop
      // delivery means the display picks it up on its refresh tick — say so.
      case 'DISPLAY_PINNED': {
        const msg = data.ok
          ? `Pinned to ${(data.displays || []).join(', ')} — appears on the display within a minute`
          : (data.reason
             || `Couldn't pin${(data.failed || []).length ? ` to ${data.failed.join(', ')}` : ''}`);
        store.dispatch(setTransientNotice(msg));
        if (transientTimer) clearTimeout(transientTimer);
        transientTimer = setTimeout(() => store.dispatch(setTransientNotice(null)), 5000);
        break;
      }
      // background-run: a scheduled job finished. If THIS session is sitting in
      // the station the job belongs to, the result belongs in the conversation
      // — the user is right there, and making them go find it on the tasks page
      // would be absurd. Any other session leaves it to the bell and the tasks
      // page, which is where it lives permanently either way.
      //
      // The server cannot make this call: push_to_account fans out to every
      // session the account has open and none of them tell it what they are
      // looking at. So the frame carries `station_id` and each session decides.
      //
      // This frame used to be dropped on the floor here (the client-side
      // results inbox was removed when the tasks page shipped, and
      // `stop-scheduled-notification-push` was queued to stop sending it).
      // It is the transport now.
      case 'MAILBOX_MESSAGE': {
        // mailbox: a station's message to this person. The inbox list is stale either way. A
        // session showing that station asks for it to be delivered into this chat now (that is
        // when it counts as read); anywhere else it waits, unread, for the next open.
        const m = data.message || {};
        store.dispatch(notedScheduledRun());
        const st = currentState.chat.station;
        const here = st && st.station && st.station.station_id;
        if (here != null && String(m.station_id) === String(here)) {
          store.dispatch(requestMailDelivery(here, st.sessionId, currentState.chat.threadId));
        }
        break;
      }
      case 'SCHEDULED_NOTIFICATION': {
        const note = data.notification || {};
        // FIRST, and deliberately before the station gate (!666 review): a run
        // finished, so anything showing a list of runs is now stale. That is
        // true whether or not this session is the one that started the job —
        // the gate below decides where the RESULT goes, not whether the tasks
        // page needs re-reading.
        store.dispatch(notedScheduledRun());
        // `station_id`, NOT `id` — that is the field the station object
        // actually carries (every sendText in ChatInterface reads
        // `station.station.station_id`). Reading `.id` here yields undefined
        // forever, so the guard below would drop every notification and this
        // whole path would look wired and do nothing.
        const here = currentState.chat.station
          && currentState.chat.station.station
          && currentState.chat.station.station.station_id;
        // A task with the station_id=0 sentinel sends null. A session with no
        // station reads undefined (chatSlice initialises `station: {}`) — or
        // NULL, if `metrics.station.station` ever arrives null, which nothing
        // rules out: it is a plain object reference on qbric's SubscriberMetrics
        // record. `null == undefined` is TRUE in JS, so `note.station_id != here`
        // would read "no station either side" as a match and deliver every
        // unrouted job to every stationless session.
        //
        // THE `== null` PAIR IS NOT REDUNDANT (!666 review corrected an earlier
        // comment here that said it was). String() coercion alone covers
        // null/undefined, because "null" !== "undefined" — but NOT null/null,
        // which is reachable through `a && a.b && a.b.station_id`, since `&&`
        // yields null rather than undefined for a null intermediate. My original
        // mutation check used undefined for `here` and so proved the wrong
        // thing. Keep both.
        if (note.station_id == null || here == null
            || String(note.station_id) !== String(here)) {
          break;
        }
        // Not a reply to anything the user asked, so — like a room message —
        // it must not disturb their own thread state (threadId / waiting /
        // prompting). Only the scrollback.
        //
        // scheduled-result-view: chat is a POINTER, not a second result viewer.
        //
        // It used to paste `message_text` here, which the scheduler truncates to
        // 200 chars (`_summarize_response`) — so a real answer arrived cut off
        // mid-sentence. It also re-rendered every card into the conversation.
        // Both are gone: the run's page on /tasks is the one place the result
        // lives, and this is a link to it.
        //
        // Deliberately NOT keeping the cards alongside the link. That was the
        // lower-risk option and it was rejected on purpose: with cards here the
        // link is redundant for exactly the results that least need it, and the
        // same answer renders twice in two places. "Results live on the Tasks
        // page" only reads as a rule if it has no exceptions.
        //
        // run-result-unfurl REVISES that rule — narrowly, and the distinction is
        // the whole point. The result still lives in exactly one place; what
        // changed is WHERE that one place opens. `embed_id` is a snapshot of
        // this run the server pre-minted (privately — see
        // `notification_watcher._mint_run_embed`), so the chat draws the same
        // preview card a pasted share link gets, and clicking it opens the
        // result in the OUTPUT PANEL beside the conversation instead of
        // navigating to /tasks and unloading the station. A second copy in the
        // scrollback is still refused: the card is behind the click, not in the
        // bubble.
        //
        // Absent (failed run, a task whose helper emits no card) → no preview,
        // and the Tasks link stands as before. That fallback is not decoration:
        // it is the only affordance a failed run has.
        //
        // The truncation itself stays — `_summarize_response` also feeds the
        // log line and the web-push body, where a short string is correct. This
        // was never a summariser bug; it was a renderer using the wrong field.
        const runId = note.id;
        store.dispatch(addMessage({
          userId: 0,
          displayName: note.task_name || 'Scheduled task',
          msgId: runId ? `sched-${runId}` : undefined,
          message: note.status === 'err' ? 'Failed' : 'Finished',
          // The chat renderer turns this into the "View result ↗" affordance.
          // A plain URL rather than a click handler: it survives a reload and a
          // paste, which a dispatch-on-click would not.
          scheduledRun: runId ? {
            runId,
            taskId: note.task_id,
            href: `/tasks/run/${encodeURIComponent(runId)}`,
            status: note.status,
            cardCount: Array.isArray(note.cards) ? note.cards.length : 0,
            // The pre-minted snapshot, when there is one. A ROOT-RELATIVE url:
            // the preview resolver only accepts same-origin share links, and a
            // relative one cannot be wrong about the host the way an absolute
            // one built from a server-side guess can.
            embedUrl: note.embed_id
              ? `/embed/s/${encodeURIComponent(note.embed_id)}`
              : undefined,
          } : undefined,
        }));
        break;
      }
      default:
        console.log('Unknown message type:', data.messageType);
    }
  };

  return (next) => (action) => {
    switch (action.type) {
      case 'WS_CONNECT':
        if (socket !== null) {
          closeDetached(socket);
        }
        clearHandshakeTimer();
        handshakeRetried = false;   // a user-initiated connect gets its own retry
        openSocket(action.payload.url);
        break;
      case 'WS_DISCONNECT':
        clearHandshakeTimer();
        if (socket !== null) {
          socket.close();
        }
        socket = null;
        break;
      case 'WS_SEND':
        socket.send(JSON.stringify(action.payload));
        break;
      default:
        return next(action);
    }
  };
};

export default websocketMiddleware;